import numbro from 'numbro';

import { InterpolateElement, T } from '../components/Interpolate';

import { Format } from './format/formats';

const TRILLION = 1e12;
const BILLION = 1e9;
const MILLION = 1e6;
const THOUSAND = 1e3;

export enum Scale {
  ONE = 'ONE',
  THOUSAND = 'THOUSAND',
  MILLION = 'MILLION',
  BILLION = 'BILLION',
  TRILLION = 'TRILLION',
}
/**
 * List of all the scale values in increasing order
 * (guaranteed order, whereas enum order can be random)
 */
export const SCALES = [Scale.ONE, Scale.THOUSAND, Scale.MILLION, Scale.BILLION, Scale.TRILLION];

function isNumber(value: number | null | undefined): value is number {
  return typeof value === 'number';
}

export const getAbbreviationFromScale = (scale: Scale): string => {
  switch (scale) {
    case Scale.THOUSAND:
      return numbro.languageData().abbreviations.thousand;
    case Scale.MILLION:
      return numbro.languageData().abbreviations.million;
    case Scale.BILLION:
      return numbro.languageData().abbreviations.billion;
    case Scale.TRILLION:
      return numbro.languageData().abbreviations.trillion;
    default:
      return '';
  }
};

const SCALE_LABELS: Record<Exclude<Scale, Scale.ONE>, InterpolateElement> = {
  [Scale.TRILLION]: T(`trillions.label`, { ns: 'scale' }),
  [Scale.BILLION]: T(`billions.label`, { ns: 'scale' }),
  [Scale.MILLION]: T(`millions.label`, { ns: 'scale' }),
  [Scale.THOUSAND]: T(`thousands.label`, { ns: 'scale' }),
};

export const getLabelFromScale = (scale: Scale): InterpolateElement | string => {
  if (scale === Scale.ONE) {
    return '';
  }
  return SCALE_LABELS[scale];
};

export const getDivisorFromScale = (scale: Scale): number => {
  switch (scale) {
    case Scale.THOUSAND:
      return THOUSAND;
    case Scale.MILLION:
      return MILLION;
    case Scale.BILLION:
      return BILLION;
    case Scale.TRILLION:
      return TRILLION;
    default:
      return 1;
  }
};

export const getScaleForValues = (values: (number | null | undefined)[]): Scale => {
  const filteredValues = values.filter(isNumber);
  const scaleCounts: Record<Scale, number> = filteredValues.reduce(
    (scaleCounts: Record<Scale, number>, value) => {
      const absValue = Math.abs(value);
      if (absValue >= TRILLION) {
        scaleCounts[Scale.TRILLION]++;
      } else if (absValue < TRILLION && absValue >= BILLION) {
        scaleCounts[Scale.BILLION]++;
      } else if (absValue < BILLION && absValue >= MILLION) {
        scaleCounts[Scale.MILLION]++;
      } else if (absValue < MILLION && absValue >= THOUSAND) {
        scaleCounts[Scale.THOUSAND]++;
      } else if (absValue > 0) {
        scaleCounts[Scale.ONE]++;
      }
      return scaleCounts;
    },
    {
      [Scale.TRILLION]: 0,
      [Scale.BILLION]: 0,
      [Scale.MILLION]: 0,
      [Scale.THOUSAND]: 0,
      [Scale.ONE]: 0,
    }
  );

  const scale = (Object.keys(scaleCounts) as Scale[]).reduce((a: Scale, b: Scale) =>
    scaleCounts[a] > scaleCounts[b] ? a : b
  );

  return scale;
};

const SCALE_FORMAT_MAP_WITHOUT_CURRENCY: Record<Scale, Format> = {
  [Scale.TRILLION]: Format.MONEY_TRILLIONS,
  [Scale.BILLION]: Format.MONEY_BILLIONS,
  [Scale.MILLION]: Format.MONEY_MILLIONS,
  [Scale.THOUSAND]: Format.MONEY_THOUSANDS,
  [Scale.ONE]: Format.MONEY_ONES,
};

const SCALE_FORMAT_MAP_WITH_CURRENCY: Record<Scale, Format> = {
  [Scale.TRILLION]: Format.MONEY_TRILLIONS_CURRENCY,
  [Scale.BILLION]: Format.MONEY_BILLIONS_CURRENCY,
  [Scale.MILLION]: Format.MONEY_MILLIONS_CURRENCY,
  [Scale.THOUSAND]: Format.MONEY_THOUSANDS_CURRENCY,
  [Scale.ONE]: Format.MONEY_ONES_CURRENCY,
};

export const getFormatFromScale = ({ scale, withCurrency }: { scale: Scale; withCurrency?: boolean }): Format => {
  const scaleFormatMap = withCurrency ? SCALE_FORMAT_MAP_WITH_CURRENCY : SCALE_FORMAT_MAP_WITHOUT_CURRENCY;

  return scaleFormatMap[scale];
};

export const getFormatForValues = ({
  values,
  withCurrency,
}: {
  values: (number | null | undefined)[];
  withCurrency?: boolean;
}): Format => {
  const scale = getScaleForValues(values);

  return getFormatFromScale({ scale, withCurrency });
};

export const scaleValue = (value: number, scale: Scale) => {
  return value / getDivisorFromScale(scale);
};
